/**
 * Página de Detalle de Formulario de Evento.
 *
 * Este componente muestra la información completa de un formulario de registro
 * de eventos, incluyendo estadísticas, configuración de fechas, link público,
 * campos personalizados y opciones de edición.
 *
 * @author Eulicer Zapata <eulicer.zapata@iudigital.edu.co>
 * @date [Fecha de creación: 2025-11-12]
 * @updated [Fecha de última modificación: 2025-11-12]
 * @updatedBy Eulicer Zapata <eulicer.zapata@iudigital.edu.co>
 */

import React from "react";
import {
  Box,
  Card,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  CircularProgress,
  Chip,
  Divider,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import ToolTip from "components/ToolTip";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import QrCodeIcon from "@mui/icons-material/QrCode";

/**
 * Componente de página de detalle del formulario de evento.
 *
 * Este componente renderiza una vista completa de la configuración del
 * formulario de registro, incluyendo estadísticas, fechas, link público,
 * lista de campos personalizados y acciones para editar la configuración.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {Object|null} props.formData - Datos completos del formulario de evento.
 * @param {Object} props.formData.data_source - Información de la fuente de datos del evento.
 * @param {string} props.formData.data_source.card_type_name - Nombre del tipo de carnet.
 * @param {number} props.formData.current_registrations - Cantidad actual de registros.
 * @param {number} props.formData.max_registrations - Límite máximo de registros.
 * @param {string} props.formData.slug - Slug para la URL pública del formulario.
 * @param {string} props.formData.form_start_date - Fecha de inicio del formulario.
 * @param {string} props.formData.form_end_date - Fecha de cierre del formulario.
 * @param {string} props.formData.card_effective_date - Fecha de activación del carnet.
 * @param {string} props.formData.card_expiration_date - Fecha de expiración del carnet.
 * @param {boolean} props.formData.is_active - Estado activo/inactivo del formulario.
 * @param {Array} props.formData.custom_fields - Array de campos personalizados del formulario.
 * @param {boolean} props.loading - Indica si se están cargando los datos.
 * @param {Function} props.copyToClipboard - Función para copiar texto al portapapeles.
 * @param {Function} props.getPublicUrl - Función que retorna la URL pública del formulario.
 * @param {Function} props.handleCreateField - Función para crear un nuevo campo personalizado.
 * @param {Function} props.handleEditField - Función para editar un campo personalizado existente.
 * @param {Function} props.handleClickDelete - Función para eliminar un campo personalizado.
 * @param {Function} props.setConfigModalOpen - Función para abrir el modal de configuración.
 * @param {boolean} props.canEdit - Indica si el usuario tiene permiso para editar el formulario.
 * @param {Function} props.handleDownloadQR - Función para descargar el código QR del formulario.
 * @returns {JSX.Element} Página con detalle completo del formulario de evento.
 */
export default function EventFormDetailPage({
  formData,
  loading,
  copyToClipboard,
  getPublicUrl,
  handleCreateField,
  handleEditField,
  handleClickDelete,
  handleCreateSection,
  handleEditSection,
  setConfigModalOpen,
  canEdit = false,
  handleDownloadQR,
}) {
  const publicUrl = getPublicUrl();

  /**
   * Formatea una fecha para mostrarla en formato legible.
   *
   * Convierte fechas en formato ISO a formato local colombiano con
   * fecha completa y hora.
   *
   * @param {string} dateStr - Fecha en formato ISO del backend.
   * @returns {string} Fecha formateada o "No definido" si está vacía.
   */
  const formatDate = (dateStr) => {
    if (!dateStr) return "No definido";
    const date = new Date(dateStr);
    return date.toLocaleString("es-CO", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /**
   * Obtiene la etiqueta legible de un tipo de campo.
   *
   * @param {string} type - Tipo del campo.
   * @returns {string} Etiqueta legible.
   */
  const getFieldTypeLabel = (type) => {
    const types = {
      alphabetic: "Alfabético",
      numeric: "Numérico",
      alphanumeric: "Alfanumérico",
      select: "Lista de Opciones",
      checklist: "Checklist",
    };
    return types[type] || type;
  };

  /**
   * Calcula el total de campos personalizados (sueltos + dentro de secciones).
   *
   * @param {Array} customFields - Array de custom_fields (puede tener campos sueltos y secciones).
   * @returns {number} Total de campos.
   */
  const getTotalFieldCount = (customFields) => {
    if (!customFields || !Array.isArray(customFields)) return 0;
    return customFields.reduce((acc, item) => {
      if (item.fields && Array.isArray(item.fields)) {
        return acc + item.fields.length;
      }
      return acc + 1;
    }, 0);
  };

  /**
   * Renderiza la tabla de campos para un array de fields.
   *
   * @param {Array} fields - Array de campos.
   * @param {string|null} sectionId - ID de la sección contenedora o null.
   * @returns {JSX.Element} Tabla con los campos.
   */
  const renderFieldsTable = (fields, sectionId) => (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "2px solid #e0e0e0" }}>
            <th
              style={{
                textAlign: "left",
                padding: "12px 8px",
                fontWeight: 500,
                fontSize: "0.75rem",
                color: "#666",
                textTransform: "uppercase",
              }}
            >
              Nombre del Campo
            </th>
            <th
              style={{
                textAlign: "left",
                padding: "12px 8px",
                fontWeight: 500,
                fontSize: "0.75rem",
                color: "#666",
                textTransform: "uppercase",
              }}
            >
              Tipo
            </th>
            <th
              style={{
                textAlign: "left",
                padding: "12px 8px",
                fontWeight: 500,
                fontSize: "0.75rem",
                color: "#666",
                textTransform: "uppercase",
              }}
            >
              Requerido
            </th>
            {canEdit && (
              <th
                style={{
                  textAlign: "center",
                  padding: "12px 8px",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                  color: "#666",
                  textTransform: "uppercase",
                  width: "120px",
                }}
              >
                Acciones
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {fields.map((field) => (
            <tr key={field.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
              <td style={{ padding: "16px 8px" }}>
                <Typography variant="body2">{field.label}</Typography>
              </td>
              <td style={{ padding: "16px 8px" }}>
                <Chip
                  label={getFieldTypeLabel(field.type)}
                  size="small"
                  variant="outlined"
                  sx={{ minWidth: "120px" }}
                />
              </td>
              <td style={{ padding: "16px 8px" }}>
                {field.required ? (
                  <Chip label="Sí" size="small" color="warning" />
                ) : (
                  <Chip label="No" size="small" />
                )}
              </td>
              {canEdit && (
                <td style={{ padding: "16px 8px", textAlign: "center" }}>
                  <Tooltip title="Editar campo">
                    <IconButton
                      size="small"
                      color="info"
                      onClick={() => handleEditField(field, sectionId)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar campo">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleClickDelete(field, "field", sectionId)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );

  /**
   * Renderiza los campos personalizados agrupados: campos sueltos primero, luego secciones.
   *
   * @param {Array} customFields - Array mixto de campos sueltos y secciones.
   * @returns {JSX.Element} Renderizado completo de campos y secciones.
   */
  const renderCustomFieldsSections = (customFields) => {
    if (!customFields || customFields.length === 0) {
      return (
        <Box textAlign="center" py={4}>
          <Typography variant="body2" color="text.secondary">
            No hay campos personalizados configurados
          </Typography>
        </Box>
      );
    }

    // Separar campos sueltos (legacy) de secciones
    const looseFields = customFields.filter((item) => !item.fields);
    const sections = customFields.filter((item) => item.fields && Array.isArray(item.fields));

    return (
      <>
        {/* Campos sueltos (sin sección) */}
        {looseFields.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <MDTypography variant="subtitle2" fontWeight="medium" sx={{ color: "#666", mb: 1 }}>
              General ({looseFields.length})
            </MDTypography>
            {renderFieldsTable(looseFields, null)}
          </Box>
        )}

        {/* Secciones */}
        {sections.map((section) => (
          <Box key={section.id} sx={{ mb: 3, border: "1px solid #e0e0e0", borderRadius: 2, p: 2 }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="subtitle1" fontWeight="medium" sx={{ color: "#00416A" }}>
                {section.name} ({section.fields?.length || 0})
              </MDTypography>
              {canEdit && (
                <MDBox display="flex" gap={1}>
                  <MDButton
                    color="info"
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => handleCreateField(section.id)}
                  >
                    Campo
                  </MDButton>
                  <Tooltip title="Editar sección">
                    <IconButton
                      size="small"
                      color="info"
                      onClick={() => handleEditSection(section)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar sección">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleClickDelete(section, "section")}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </MDBox>
              )}
            </MDBox>

            {section.fields && section.fields.length > 0 ? (
              renderFieldsTable(section.fields, section.id)
            ) : (
              <Box textAlign="center" py={2}>
                <Typography variant="body2" color="text.secondary">
                  No hay campos en esta sección
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </>
    );
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
        p={3}
      >
        <CircularProgress size={50} />
        <Typography variant="body1" mt={2}>
          Cargando configuración...
        </Typography>
      </Box>
    );
  }

  if (!formData) {
    return (
      <MDBox p={3}>
        <Card
          sx={{
            backgroundColor: "#E3F2FD",
            border: "2px solid #00416A",
            borderRadius: 2,
            p: 4,
          }}
        >
          <MDBox display="flex" flexDirection="column" alignItems="center" textAlign="center">
            <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#00416A", mb: 2 }}>
              Formulario No Disponible
            </MDTypography>
            <MDTypography variant="body1" sx={{ color: "#00416A", mb: 2 }}>
              Este evento no cuenta con formulario de registro público.
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: "#546E7A" }}>
              Solo los eventos con tipo de carnet <strong>"Invitado"</strong> tienen formulario de
              registro público habilitado. Los eventos creados con otros tipos de carnet no disponen
              de esta funcionalidad.
            </MDTypography>
          </MDBox>
        </Card>
      </MDBox>
    );
  }

  return (
    <MDBox px={3}>
      {/* Estadísticas - Header */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3 }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Tipo de Carnet
            </Typography>
            <Typography variant="h6" fontWeight="medium">
              {formData?.data_source?.card_type_name || "N/A"}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3 }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Registros Actuales
            </Typography>
            <Typography variant="h4" fontWeight="bold" color="info">
              {formData?.current_registrations || 0}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3 }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Máximo de Registros
            </Typography>
            <Typography variant="h4" fontWeight="bold" color="success">
              {formData?.max_registrations || "∞"}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Columna Principal - Información Completa */}
        <Grid item xs={12}>
          {/* Link Público */}
          {formData?.slug && (
            <Card sx={{ mb: 3, p: 3 }}>
              <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <MDTypography variant="h6" fontWeight="medium">
                  Link Público de Registro
                </MDTypography>
              </MDBox>
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  fullWidth
                  value={publicUrl}
                  InputProps={{ readOnly: true }}
                  size="small"
                />
                <Tooltip title="Copiar link">
                  <IconButton onClick={() => copyToClipboard(publicUrl)} color="info">
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Abrir en nueva pestaña">
                  <IconButton onClick={() => window.open(publicUrl, "_blank")} color="info">
                    <OpenInNewIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Descargar código QR">
                  <IconButton onClick={handleDownloadQR} color="info">
                    <QrCodeIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Card>
          )}

          {/* Configuración del Formulario */}
          <Card sx={{ mb: 3, p: 3 }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <MDTypography variant="h6" fontWeight="medium">
                Configuración del Formulario
              </MDTypography>
              {canEdit && (
                <MDButton
                  variant="gradient"
                  color="info"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => setConfigModalOpen(true)}
                >
                  Editar
                </MDButton>
              )}
            </MDBox>

            {/* Sección: Fechas del Formulario */}
            <MDBox mb={3}>
              <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                <MDTypography variant="h6" fontWeight="medium" sx={{ color: "#00416A" }}>
                  Fechas del Formulario de Registro
                </MDTypography>
                <ToolTip message="Define el período durante el cual el formulario estará disponible para registros públicos" />
              </MDBox>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    display="block"
                    mb={0.5}
                    fontWeight="medium"
                  >
                    Fecha de Inicio
                  </Typography>
                  <Typography variant="body2">{formatDate(formData.form_start_date)}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    display="block"
                    mb={0.5}
                    fontWeight="medium"
                  >
                    Fecha de Cierre
                  </Typography>
                  <Typography variant="body2">{formatDate(formData.form_end_date)}</Typography>
                </Grid>
              </Grid>
            </MDBox>

            {/* Sección: Fechas del Carnet */}
            <MDBox mb={3}>
              <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                <MDTypography variant="h6" fontWeight="medium" sx={{ color: "#00416A" }}>
                  Vigencia del Carnet
                </MDTypography>
                <ToolTip message="Define el período de validez de los carnets de los usuarios registrados por el formulario público" />
              </MDBox>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    display="block"
                    mb={0.5}
                    fontWeight="medium"
                  >
                    Fecha de Activación
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(formData.card_effective_date)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    display="block"
                    mb={0.5}
                    fontWeight="medium"
                  >
                    Fecha de Expiración
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(formData.card_expiration_date)}
                  </Typography>
                </Grid>
              </Grid>
            </MDBox>

            {/* Sección: Configuración General */}
            <MDBox>
              <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                <MDTypography variant="h6" fontWeight="medium" sx={{ color: "#00416A" }}>
                  Configuración General
                </MDTypography>
                <ToolTip message="Parámetros adicionales del evento como límite de registros y estado de disponibilidad" />
              </MDBox>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    display="block"
                    mb={0.5}
                    fontWeight="medium"
                  >
                    Máximo de Registros
                  </Typography>
                  <Typography variant="body2">
                    {formData.max_registrations || "Ilimitado"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    display="block"
                    mb={0.5}
                    fontWeight="medium"
                  >
                    Estado del Formulario
                  </Typography>
                  <Chip
                    label={formData.is_active ? "Activo" : "Inactivo"}
                    size="small"
                    color={formData.is_active ? "success" : "error"}
                  />
                </Grid>
              </Grid>
            </MDBox>
          </Card>

          {/* Campos Personalizados por Secciones */}
          <Card sx={{ p: 3 }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <MDTypography variant="h6" fontWeight="medium">
                Campos Personalizados ({getTotalFieldCount(formData.custom_fields)})
              </MDTypography>
              {canEdit && (
                <MDBox display="flex" gap={1}>
                  <MDButton
                    color="info"
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={handleCreateSection}
                  >
                    Nueva Sección
                  </MDButton>
                  <MDButton
                    color="success"
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => handleCreateField(null)}
                  >
                    Campo
                  </MDButton>
                </MDBox>
              )}
            </MDBox>

            {renderCustomFieldsSections(formData.custom_fields)}
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}
